import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Box,
  ListItemIcon,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import { getUserInfo } from "../api/getCommands";
import { googleSignIn, googleLogout } from "../api/postCommands";
import { useSettings } from "../context/SettingsContext";

interface UserInfo {
  id: number;
  displayName: string;
  email: string;
  profilePicture: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export default function GoogleSignIn() {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { settings } = useSettings();

  // Check if already logged in
  useEffect(() => {
    getUserInfo()
      .then((data) => {
        if (data?.user) setUser(data.user);
      })
      .catch(() => {});
  }, []);

  const handleCredentialResponse = useCallback(async (response: { credential: string }) => {
    try {
      const data = await googleSignIn(response.credential);
      if (data.success && data.user) {
        setUser(data.user);
      }
    } catch (err) {
      console.error("Sign-in error:", err);
    }
  }, []);

  // Initialize Google Identity Services
  useEffect(() => {
    const initGoogle = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || "",
          callback: handleCredentialResponse,
        });
      }
    };

    // Script may already be loaded or may still be loading
    if (window.google?.accounts?.id) {
      initGoogle();
    } else {
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          initGoogle();
          clearInterval(interval);
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [handleCredentialResponse]);

  function handleSignIn() {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.prompt();
    }
  }

  async function handleLogout() {
    setAnchorEl(null);
    try {
      await googleLogout();
      setUser(null);
    } catch (err) {
      console.error("Logout error:", err);
    }
  }

  if (!user) {
    return (
      <Button
        id="google-signin-trigger"
        onClick={handleSignIn}
        sx={{ color: "grey.400" }}
        startIcon={<LoginIcon />}
      >
        Sign In
      </Button>
    );
  }

  return (
    <>
      <Button
        id="google-user-btn"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: "grey.400", textTransform: "none" }}
        startIcon={
          user.profilePicture ? (
            <Avatar src={user.profilePicture} sx={{ width: 28, height: 28 }} />
          ) : (
            <PersonIcon />
          )
        }
      >
        <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
          {settings.streamerMode ? user.displayName.split(" ")[0] : user.displayName}
        </Typography>
      </Button>
      <Menu
        id="google-user-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem disabled>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {settings.streamerMode ? user.displayName.split(" ")[0] : user.displayName}
            </Typography>
            {!settings.streamerMode && (
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            )}
          </Box>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>
    </>
  );
}
